/**
 * BlurImage Component
 * Implements lazy loading with blur-up placeholder effect
 * Uses Intersection Observer for efficient lazy loading
 * 
 * @param {boolean} priority - If true, loads immediately without lazy loading (for above-the-fold content)
 */
import { useState, useRef, useEffect } from 'react';

const BlurImage = ({ 
  src, 
  alt = '', 
  className = '', 
  containerClassName = '',
  placeholderColor = '#E8E6E1', // site-bg-alt equivalent
  priority = false, // Load immediately for above-the-fold content
  onLoad,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Start in view if priority
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading (skip if priority)
  useEffect(() => {
    if (priority) return; // Skip observer for priority images
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Preload priority images
  useEffect(() => {
    if (priority && src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      
      return () => {
        // Cleanup preload link after component unmounts
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [priority, src]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${containerClassName}`}
      style={{ backgroundColor: placeholderColor }}
    >
      {/* Blur placeholder - visible until image loads */}
      <div 
        className={`absolute inset-0 transition-opacity duration-500 ease-out ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ backgroundColor: placeholderColor }}
      >
        {/* Animated shimmer effect - shorter duration for priority images */}
        {!priority && (
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        )}
      </div>

      {/* Actual image - load immediately if priority, otherwise when in view */}
      {isInView && !hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`transition-all duration-500 ease-out ${
            isLoaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-sm scale-105'
          } ${className}`}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          {...props}
        />
      )}

      {/* Error fallback */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-site-bg-alt">
          <span className="text-site-text-muted text-xs font-mono">Image not found</span>
        </div>
      )}
    </div>
  );
};

export default BlurImage;
